import React, { useState } from "react";
import { FaMinus } from "react-icons/fa6";
import { FiPlus } from "react-icons/fi";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import Tabs from "../shared/Tabs";
import { trackApplyButton } from '@/utils/dataLayer';

const FAQItem = ({ item, isOpen, onToggle }) => {
  return (
    <div
      className="rounded-[12px] p-4 transition-all duration-300 bg-[#F5F7F9]"
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between text-left"
      >
        <p className="text-[14px] xl:text-[18px] font-[500] text-gray-700 flex-1">
          {item.title}
        </p>

        <div className="w-6 h-6 flex items-center justify-center">
          {isOpen ? (
            <FaMinus className="text-[#936EFE] text-[24px]" />
          ) : (
            <FiPlus className="text-black text-[24px]" />
          )}
        </div>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[500px] opacity-100 mt-5" : "max-h-0 opacity-0"
          }`}
      >
        <p className="text-[15px] font-[400] text-[#8B8B8B] leading-relaxed">
          {item.description}
        </p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  const [openItemId, setOpenItemId] = useState(null);

  const data = {
    advantages: [
      {
        id: 1,
        title: t("home-page.faq-tab-advantages-1"),
        description:
          t("home-page.faq-tab-advantages-1-info"),
      },
      {
        id: 2,
        title: t("home-page.faq-tab-advantages-2"),
        description:
          t("home-page.faq-tab-advantages-2-info"),
      },
      {
        id: 3,
        title: t("home-page.faq-tab-advantages-3"),
        description:
          t("home-page.faq-tab-advantages-3-info"),
      }
    ],

    opportunities: [
      {
        id: 1,
        title: t("home-page.faq-tab-opportunities-1"),
        description:
          t("home-page.faq-tab-opportunities-1-info"),
      },
      {
        id: 2,
        title: t("home-page.faq-tab-opportunities-2"),
        description:
          t("home-page.faq-tab-opportunities-2-info"),
      },
      {
        id: 3,
        title: t("home-page.faq-tab-opportunities-3"),
        description:
          t("home-page.faq-tab-opportunities-3-info"),
      }
    ],
  };

  const toggleItem = (id) => {
    setOpenItemId((prev) => (prev === id ? null : id));
  };

  const handleTabChange = (tabId) => {
    setOpenItemId(null);
  };

  const tabs = [
    { id: "advantages", label: t("home-page.faq-tab-advantages") },
    { id: "opportunities", label: t("home-page.faq-tab-opportunities") },
  ];

  return (
    <section className="pb-[100px] md:pb-[200px] bg-white max-w-[90%] mx-auto">
      <div className="flex flex-col lg:flex-row gap-[50px] lg:justify-between">

        <div className="flex flex-col">
          <h2 className="text-[32px] md:text-[48px] lg:text-[64px] xl:text-[80px] font-[500] leading-tight flex flex-col">
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
              {t("home-page.faq-title-1")}
            </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
              {t("home-page.faq-title-2")}
            </span>
          </h2>

          <div className="flex flex-col gap-[24px] mt-6">
            <p className="text-[16px] lg:text-[20px] text-[#8B8B8B] leading-relaxed">
              {t("home-page.faq-description")}
            </p>

            <Link href="/apply">
              <button className="w-full md:w-[200px] h-[48px] md:h-[56px] px-6 rounded-full bg-main-pink border border-main-pink text-white text-[16px] font-[500] transition duration-300 hover:!bg-white hover:!text-main-pink">
                {t("register-now")}
              </button>
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-[24px] w-full lg:max-w-[50%] xl:max-w-[40%]">
          <Tabs
            tabs={tabs}
            defaultTab="advantages"
            onTabChange={handleTabChange}
          >
            {(activeTab) => {
              const currentData = data[activeTab];
              return (
                <div className="flex flex-col gap-4">
                  {currentData.map((item) => (
                    <FAQItem
                      key={item.id}
                      item={item}
                      isOpen={openItemId === item.id}
                      onToggle={() => toggleItem(item.id)}
                    />
                  ))}
                </div>
              );
            }}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
